import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-route SEO for this client-rendered SPA: every route gets a unique <title>,
// meta description, canonical URL and social tags. Without this all routes would
// share index.html's single title/description (the "duplicate title/description"
// SEO issue). Google and modern SEO crawlers execute JS and read these updates.
const SITE = "https://roadrnr.app";

const DEFAULT = {
  title: "RoadRnr — Car & Motorcycle Events",
  description:
    "Discover car meets, motorcycle events, shows, track days and automotive events near you.",
};

const EVENT = {
  title: "Event Details | RoadRnr",
  description:
    "View the date, location, tickets and details for this car or motorcycle event on RoadRnr, save it and add it to your calendar.",
};

const META = {
  "/": {
    title: "RoadRnr — Discover Car & Motorcycle Events Near You",
    description:
      "Find car meets, bike nights, shows, track days and automotive events near you on an interactive map. Save events, add them to your calendar and never miss a meet.",
  },
  "/search": {
    title: "Search Car & Motorcycle Events | RoadRnr",
    description:
      "Search RoadRnr for car meets, motorcycle events, shows and track days by name, location or category, and find your next automotive event.",
  },
  "/calendar": {
    title: "Automotive Events Calendar | RoadRnr",
    description:
      "Browse upcoming car and motorcycle events day by day on the RoadRnr calendar and plan which meets, shows and track days to attend.",
  },
  "/saved": {
    title: "Your Saved Events | RoadRnr",
    description:
      "View the car and motorcycle events you have saved on RoadRnr and keep track of the meets, shows and track days you plan to attend.",
  },
  "/login": {
    title: "Log In to RoadRnr",
    description:
      "Log in to RoadRnr to save events, submit your own automotive events and manage your listings and banner ads.",
  },
  "/signup": {
    title: "Create Your RoadRnr Account",
    description:
      "Sign up for a free RoadRnr account to save car and motorcycle events, submit listings and advertise to automotive enthusiasts.",
  },
  "/add-event": {
    title: "Add Your Automotive Event | RoadRnr",
    description:
      "List your car meet, motorcycle event, show or track day on RoadRnr for free and reach automotive enthusiasts near you.",
  },
  "/advertise": {
    title: "Advertise on RoadRnr — Banner Ads for Automotive Brands",
    description:
      "Promote your brand, products or services to automotive enthusiasts with a 7-day banner ad on RoadRnr for £9.99.",
  },
  "/my-banner-ads": {
    title: "My Banner Ads | RoadRnr",
    description: "Manage, edit, end or re-run your RoadRnr banner ad campaigns.",
  },
  "/my-events": {
    title: "My Events | RoadRnr",
    description:
      "Manage the automotive events you have submitted to RoadRnr, edit their details and boost your listings.",
  },
  "/profile": {
    title: "Your Profile | RoadRnr",
    description: "Manage your RoadRnr profile, saved events and account settings.",
  },
  "/privacy": {
    title: "Privacy Policy | RoadRnr",
    description:
      "Read the RoadRnr privacy policy to understand how we collect, use and protect your personal data.",
  },
  "/terms": {
    title: "Terms of Service | RoadRnr",
    description:
      "Read the RoadRnr terms of service covering use of the platform, event listings and banner advertising.",
  },
  "/admin": {
    title: "Admin | RoadRnr",
    description: "RoadRnr administration and moderation dashboard.",
  },
  "/reset-password": {
    title: "Reset Your Password | RoadRnr",
    description: "Set a new password for your RoadRnr account.",
  },
};

function setMeta(key, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    let m;
    if (pathname.startsWith("/event/")) m = EVENT;
    else if (pathname.startsWith("/edit-event/")) m = META["/add-event"];
    else m = META[pathname] || DEFAULT;

    document.title = m.title;
    setMeta("description", m.description);
    setMeta("og:title", m.title, "property");
    setMeta("og:description", m.description, "property");
    setMeta("og:url", SITE + pathname, "property");
    setMeta("twitter:title", m.title);
    setMeta("twitter:description", m.description);
    setCanonical(SITE + (pathname === "/" ? "/" : pathname));

    // GA4 SPA page view: index.html sets send_page_view:false, so every route
    // change (including the first load) sends exactly one page_view here. This
    // reuses the existing single gtag/Consent Mode setup — no re-init, no consent
    // change — so client-side navigation is now measurable in GA4.
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_title: m.title,
        page_path: pathname,
        page_location: window.location.href,
      });
    }
  }, [pathname]);

  return null;
}
